import React, { useEffect, useRef, useState } from 'react';
import { AimieRobotAvatarIcon, UserAvatarIcon } from '../chat/ChatAvatarIcons';

const DEFAULT_DEMO_TURNS = [
  {
    speaker: 'client',
    text: 'I know I should quit, but it helps when I feel stressed.',
  },
  {
    speaker: 'clinician',
    text: 'Part of you wants the change, and part of you leans on it to cope.',
    chip: 'CR',
    chipClassName: 'anonymous-landing__demo-chip--cr',
  },
  {
    speaker: 'client',
    text: "Exactly. I just don't know if I'm ready yet.",
  },
  {
    speaker: 'clinician',
    text: 'What would help you feel more ready?',
    chip: 'Q',
    chipClassName: 'anonymous-landing__demo-chip--q',
  },
];

const THINKING_DELAY_MS = 850;
const STREAM_CHAR_MS = 22;
const BEFORE_TRAINEE_TURN_MS = 1400;
const AFTER_FEEDBACK_BEFORE_NEXT_CLIENT_MS = 1200;
const LOOP_RESTART_MS = 2500;

const createInitialTurnStates = (turns) => turns.map(() => ({
  isVisible: false,
  isThinking: false,
  isStreaming: false,
  displayedText: '',
}));

function waitWithTimeout(ms, registerTimeout) {
  return new Promise((resolve) => {
    registerTimeout(resolve, ms);
  });
}

function ThinkingIndicator() {
  const word = 'Contemplating';

  return (
    <span className="anonymous-landing__demo-thinking" aria-label={word}>
      {word.split('').map((char, index) => (
        <span
          key={index}
          className="anonymous-landing__demo-thinking-char"
          style={{ animationDelay: `${index * 0.1}s` }}
          aria-hidden="true"
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );
}

function DemoTurn({ turn, isVisible, displayedText, isThinking, isStreaming, visualMode, avatarMode, extraClassName = '' }) {
  if (!isVisible) {
    return null;
  }

  const isClient = turn.speaker === 'client';
  const isLeftAligned = visualMode === 'reverse' ? !isClient : isClient;
  const speakerLabel = isClient ? 'Client' : 'Clinician';
  const speakerClassName = isClient ? 'client' : 'clinician';
  const avatar = avatarMode === 'feedback'
    ? (isClient
      ? <AimieRobotAvatarIcon />
      : <UserAvatarIcon avatarId="human-curly" avatarBackground="155" />)
    : (isClient
      ? <UserAvatarIcon avatarId="human-curly" avatarBackground="155" />
      : <AimieRobotAvatarIcon />);

  return (
    <div className={`anonymous-landing__demo-turn anonymous-landing__demo-turn--speaker-${speakerClassName} anonymous-landing__demo-turn--enter${extraClassName ? ` ${extraClassName}` : ''}`}>
      {isLeftAligned && (
        <span className="anonymous-landing__demo-avatar" aria-hidden="true">
          {avatar}
        </span>
      )}
      <div className="anonymous-landing__demo-turn-content">
        <span className="anonymous-landing__demo-speaker">{speakerLabel}</span>
        <div className={`anonymous-landing__demo-bubble${isStreaming ? ' anonymous-landing__demo-bubble--streaming' : ''}${isThinking ? ' anonymous-landing__demo-bubble--thinking' : ''}`}>
          {isThinking ? (
            <ThinkingIndicator />
          ) : (
            <>
              {displayedText}
              {turn.chip && displayedText === turn.text && (
                <div className="anonymous-landing__demo-chips">
                  <span className={`anonymous-landing__demo-chip ${turn.chipClassName}`}>{turn.chip}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!isLeftAligned && (
        <span className="anonymous-landing__demo-avatar" aria-hidden="true">
          {avatar}
        </span>
      )}
    </div>
  );
}

export default function LandingDemoConversation({ onClinicianTurnShown, onTurnShown, onLoopReset, turns = DEFAULT_DEMO_TURNS, visualMode = 'default', avatarMode = 'transcript', getTurnClassName }) {
  const [turnStates, setTurnStates] = useState(() => createInitialTurnStates(turns));
  const conversationRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const timeoutIds = [];

    const registerTimeout = (callback, ms) => {
      const id = window.setTimeout(() => {
        if (!cancelled) {
          callback();
        }
      }, ms);
      timeoutIds.push(id);
    };

    const setTurnState = (index, nextState) => {
      setTurnStates((current) => current.map((turnState, turnIndex) => (
        turnIndex === index ? { ...turnState, ...nextState } : turnState
      )));
    };

    const streamAnimatedTurn = async (index) => {
      const turn = turns[index];
      setTurnState(index, {
        isVisible: true,
        isThinking: true,
        isStreaming: false,
        displayedText: '',
      });

      await waitWithTimeout(THINKING_DELAY_MS, registerTimeout);
      setTurnState(index, {
        isThinking: false,
        isStreaming: true,
        displayedText: '',
      });

      for (let i = 1; i <= turn.text.length; i += 1) {
        await waitWithTimeout(STREAM_CHAR_MS, registerTimeout);
        setTurnState(index, {
          displayedText: turn.text.slice(0, i),
        });
      }

      await waitWithTimeout(120, registerTimeout);
      setTurnState(index, {
        isStreaming: false,
        displayedText: turn.text,
      });
    };

    const showInstantTurn = async (index) => {
      const turn = turns[index];
      setTurnState(index, {
        isVisible: true,
        isThinking: false,
        isStreaming: false,
        displayedText: turn.text,
      });
      if (onTurnShown) {
        await onTurnShown(index, turn);
      }
      if (onClinicianTurnShown) {
        await onClinicianTurnShown(index);
      }
    };

    const runSequence = async () => {
      while (!cancelled) {
        setTurnStates(createInitialTurnStates(turns));
        onLoopReset?.();
        await streamAnimatedTurn(0);
        await waitWithTimeout(BEFORE_TRAINEE_TURN_MS, registerTimeout);
        await showInstantTurn(1);
        await waitWithTimeout(AFTER_FEEDBACK_BEFORE_NEXT_CLIENT_MS, registerTimeout);
        await streamAnimatedTurn(2);
        await waitWithTimeout(BEFORE_TRAINEE_TURN_MS, registerTimeout);
        await showInstantTurn(3);
        await waitWithTimeout(LOOP_RESTART_MS, registerTimeout);
      }
    };

    runSequence();

    return () => {
      cancelled = true;
      timeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, [onClinicianTurnShown, onLoopReset, onTurnShown, turns, visualMode]);

  useEffect(() => {
    const conversationEl = conversationRef.current;
    if (!conversationEl) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      conversationEl.scrollTop = conversationEl.scrollHeight;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [turnStates]);

  return (
    <div className="anonymous-landing__demo-conversation" ref={conversationRef}>
      {turns.map((turn, index) => (
        <DemoTurn
          key={`${turn.speaker}-${index}`}
          turn={turn}
          isVisible={turnStates[index]?.isVisible}
          displayedText={turnStates[index]?.displayedText}
          isThinking={turnStates[index]?.isThinking}
          isStreaming={turnStates[index]?.isStreaming}
          visualMode={visualMode}
          avatarMode={avatarMode}
          extraClassName={getTurnClassName ? getTurnClassName(index, turn) : ''}
        />
      ))}
    </div>
  );
}
